import { useCurrentLanguage } from '@/hooks/use-current-language';
import { usePollResponse } from '@/hooks/use-poll-response';
import { SupportedLanguageType } from '@/utils/intl';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import {
    MediaProgramGuide,
    WorkflowStepDataContentPack,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import { Check } from '@mui/icons-material';
import { LoadingDots } from '@rhg/components/animations/loading-dots';
import { AllGuidesCompleted } from '@rhg/components/guide/completed-dialogs/all-guides-completed';
import { TrailerCard } from '@rhg/components/guide/horizontal-minimal-card';
import { GuideListItem } from '@rhg/components/guide/list-item';
import { largeButtonCardWidth } from '@rhg/components/pack-page/pack-carousel-section';
import { PollQuestionResponse } from '@rhg/components/poll/question';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import useConfirmation from '@rhg/hooks/use-confirmation';
import { usePoll } from '@rhg/hooks/use-poll';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUnsponsoredDialog } from '@rhg/hooks/use-unsponsored-dialog';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Stack,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { LockThinOutline } from '@rhg/libs/material/icons';
import { displayLocaleDateString } from '@rhg/utils/date-time';
import { getMediaType, MediaType } from '@rhg/utils/media';
import { ProgramState } from '@rhg/utils/programs';
import { parseISO } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';
import { NextButtonOnClickHandler } from '../next-button/types';
import { PacksCarouselSection } from '../packs-carousel-section';

const MAX_RELATED_COUNT = 9;

const styleCompletedContainer = {
    mb: 6,
    padding: 6,
};

const styleCompletedIconContainer = {
    borderRadius: '50%',
    color: 'primary.main',
    padding: 3,
    width: '64px',
    height: '64px',
    mb: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.info.light
            : theme.palette.info.dark,
};

const styleCompletedIcon = {
    width: '40px',
    height: '40px',
};

const styleCompletedTitle = {
    mt: 4,
    fontWeight: (theme: Theme) => theme.typography.fontWeightBold,
};

const styleCompletedBody = {
    mt: 2,
    color: 'text.sub',
};
const styleDivider = { width: '100%', my: DS_SPACING_UNITS.CONTENT };
const styleNextButton = { width: '100%' };
const styleGuideList = { p: 0, m: 0, mb: DS_SPACING_UNITS.CONTENT };

function RatingButton({ npsPollId }: { npsPollId: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const { formatMessage } = useIntl();

    const handleClose = () => setIsOpen(false);
    const handleOpen = () => setIsOpen(true);

    return (
        <>
            <Stack alignItems="center">
                <Box>
                    <Button
                        trackButton="NotMapped"
                        variant="outlined"
                        size="small"
                        sx={{ px: '32px' }}
                        onClick={handleOpen}
                    >
                        {formatMessage(programMessages['program.rate_program'])}
                    </Button>
                </Box>
            </Stack>
            {isOpen && (
                <AllGuidesCompleted
                    isOpen
                    onClose={handleClose}
                    npsPollId={npsPollId}
                    showUpdatePlan={false}
                />
            )}
        </>
    );
}

function CompletionInfo({ workflow }: { workflow?: WorkflowWorkflowInfo }) {
    const { formatMessage } = useIntl();
    const data = workflow?.steps[0].stepDefn
        .data as WorkflowStepDataContentPack;
    const { npsPollId } = data;
    const { pollResponse, isLoading: isLoadingResponse } =
        usePollResponse(npsPollId);
    const { poll, isLoading: isLoadingPoll } = usePoll(npsPollId);

    const formattedCompletionDate = useMemo(
        () =>
            workflow?.workflowInstance?.completedAt &&
            displayLocaleDateString(
                parseISO(workflow?.workflowInstance?.completedAt)
            ),
        [workflow?.workflowInstance?.completedAt]
    );

    if (isLoadingResponse || isLoadingPoll) {
        return <LoadingDots />;
    }

    const answer = pollResponse?.polls?.[0]?.questions?.[0]?.answer;
    const rating = answer?.range;
    const question = poll?.questions?.[0];

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="subtitle2" sx={styleCompletedBody}>
                    {formatMessage(
                        programMessages['program.program_completed_body'],
                        {
                            date: formattedCompletionDate,
                        }
                    )}{' '}
                    {rating &&
                        formatMessage(
                            programMessages['program.program_completed_rated']
                        )}
                </Typography>
            </Stack>
            {question &&
                (rating ? (
                    <PollQuestionResponse question={question} answer={answer} />
                ) : (
                    <RatingButton npsPollId={npsPollId} />
                ))}
        </Stack>
    );
}
interface ProgramScheduleProps {
    onJoin: () => void;
}
export function ProgramSchedule({ onJoin }: ProgramScheduleProps) {
    const { hasPermission } = useActiveCircle();
    const {
        orderedSessions,
        workflow,
        programState,
        programType,
        program,
        packClass,
    } = useProvidedProgramContext();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { showRegistrationDialog } = useRegistrationDialog();
    const { handleNextButton, playGuide } = useProgramActions();
    const { confirm } = useConfirmation();
    const { formatMessage } = useIntl();
    const programId = program?.id ?? '';
    const currentLanguage = useCurrentLanguage();

    const isAbleToPlayPrograms = hasPermission('clinical_program', 'u');

    const canInteract = useMemo(
        () =>
            programType !== 'clinical_program' ||
            programState === ProgramState.InProgress ||
            programState === ProgramState.Completed,
        [programState, programType]
    );

    const handleUnsponsored = useCallback(async () => {
        if (await showUnsponsoredDialog()) {
            showRegistrationDialog({
                currentStep: RegistrationFlowStep.AccessCode,
                registrationFlow: RegistrationFlow.ExistingRegistration,
            });
        }
    }, [showUnsponsoredDialog, showRegistrationDialog]);

    const handleNextButtonSchedule: NextButtonOnClickHandler = (args) => {
        if (!isAbleToPlayPrograms) {
            handleUnsponsored();
        } else {
            handleNextButton()(args);
        }
    };

    const handleGuide = useCallback(
        async (guide: MediaProgramGuide) => {
            if (!isAbleToPlayPrograms) {
                handleUnsponsored();
            } else if (canInteract) {
                playGuide(guide);
            } else if (
                await confirm({
                    noCancelButton: true,
                    icon: <LockThinOutline fontSize="large" color="inherit" />,
                    iconColor: 'info',
                    title: formatMessage(
                        programMessages['program.join_program']
                    ),
                    body: formatMessage(
                        programMessages['program.before_viewing_join']
                    ),
                    confirmLabel: formatMessage(
                        programMessages['program.join_program']
                    ),
                    trackDialog: 'JoinBeforeViewingProgram',
                    confirmTrackButton: 'DialogJoinProgram',
                })
            ) {
                onJoin();
            }
        },
        [
            canInteract,
            confirm,
            handleUnsponsored,
            isAbleToPlayPrograms,
            onJoin,
            playGuide,
            formatMessage,
        ]
    );

    const trailer = useMemo(
        () => orderedSessions?.find((guide) => guide.isTrailer),
        [orderedSessions]
    );

    const nonTrailerGuides = useMemo(
        () => orderedSessions?.filter((guide) => !guide.isTrailer),
        [orderedSessions]
    );

    const relatedPacks = useMemo(
        () =>
            workflow?.steps
                .map((step) =>
                    'supplementalIds' in step.stepDefn.data
                        ? step.stepDefn.data.supplementalIds
                        : []
                )
                .flat(),
        [workflow]
    );
    const isVideo = useMemo(
        () => getMediaType(nonTrailerGuides[0]) === MediaType.Video,
        [nonTrailerGuides]
    );

    const isCompleted = programState === ProgramState.Completed;
    const shouldShowRelatedPacks =
        relatedPacks &&
        relatedPacks?.length &&
        currentLanguage === SupportedLanguageType.EN;

    return (
        <Grid container justifyContent="center" mb={DS_SPACING_UNITS.SECTION}>
            {isCompleted && (
                <Grid item xs={12}>
                    <Paper sx={styleCompletedContainer}>
                        <Stack alignItems="center">
                            <Box sx={styleCompletedIconContainer}>
                                <Check sx={styleCompletedIcon} color="info" />
                            </Box>
                            <Typography
                                variant="body1"
                                sx={styleCompletedTitle}
                            >
                                {formatMessage(
                                    programMessages[
                                        'program.program_completed_title'
                                    ]
                                )}
                            </Typography>
                            <CompletionInfo workflow={workflow} />
                        </Stack>
                    </Paper>
                </Grid>
            )}
            <Grid item md={9} sm={12}>
                <ProgramNextButton
                    trackButton="ProgramScheduleNext"
                    onClick={handleNextButtonSchedule}
                    sx={styleNextButton}
                />
            </Grid>
            <Grid item xs={12}>
                <Divider
                    aria-hidden
                    orientation="horizontal"
                    sx={styleDivider}
                />
            </Grid>
            {trailer && (
                <Grid item xs={12}>
                    <TrailerCard
                        guide={trailer}
                        onPlay={() =>
                            playGuide(trailer, {
                                willSkipToPreviousProgress: false,
                            })
                        }
                        preferNarratorImage={!isVideo}
                    />
                    <Divider
                        aria-hidden
                        sx={styleDivider}
                        orientation="horizontal"
                    />
                </Grid>
            )}
            <Grid item xs={12} component="ul" sx={styleGuideList}>
                {nonTrailerGuides.map((guide, i) => (
                    <GuideListItem
                        key={i}
                        guide={guide}
                        programId={programId}
                        programState={programState}
                        packClass={packClass}
                        programType={programType}
                        onJoin={onJoin}
                        onClick={handleGuide}
                    />
                ))}
            </Grid>
            {shouldShowRelatedPacks ? (
                <PacksCarouselSection
                    cardWidth={largeButtonCardWidth}
                    title={formatMessage(programMessages['program.related'])}
                    packClasses={relatedPacks}
                    maxCardLimit={MAX_RELATED_COUNT}
                />
            ) : null}
            {/*pollWorkflowSteps?.map((pollStep, i) => (
                <Grid key={i} item xs={12}>
                    <PollStepCard
                        step={pollStep}
                        sx={stylePollCard}
                        onClick={handleWorkflowStep}
                    />
                </Grid>
                ))*/}
        </Grid>
    );
}
